import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import api from "../services/api";
import { useToast } from "./ToastContext";

const TopicContext = createContext();

export const TopicProvider = ({ children }) => {
  const [topics, setTopics] = useState([]);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  // Use ref to track if topics are loaded
  const topicsLoaded = useRef(false);

  const getTopics = useCallback(async (forceRefresh = false) => {
    // Only fetch if we don't have topics or if forceRefresh is true
    if (topicsLoaded.current && !forceRefresh) {
      return topics;
    }

    setLoading(true);
    try {
      const response = await api.get("/topics");
      const newTopics = response.data;
      setTopics(newTopics);
      topicsLoaded.current = true;
      return newTopics;
    } catch (error) {
      console.error("Error fetching topics:", error);
      showToast(
        error.response?.data?.message || "Failed to fetch topics",
        "error"
      );
      return [];
    } finally {
      setLoading(false);
    }
  }, [showToast]); // We can safely remove topics from dependencies since we're using topicsLoaded.current

  const createTopic = useCallback(async (name) => {
    setLoading(true);
    try {
      const response = await api.post("/topics", { name });
      // Update topics list immediately
      setTopics(prevTopics => [...prevTopics, response.data]);
      topicsLoaded.current = true;
      return response.data;
    } catch (error) {
      console.error("Error creating topic:", error);
      const errorMessage = error.response?.data?.message || error.message;
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteTopic = useCallback(async (id) => {
    try {
      await api.delete(`/topics/${id}`);
      // Update topics list immediately after deletion
      setTopics(prevTopics => prevTopics.filter(topic => topic.id !== parseInt(id)));
      if (currentTopic?.id === parseInt(id)) {
        setCurrentTopic(null);
      }
    } catch (error) {
      console.error("Error deleting topic:", error);
      throw error;
    }
  }, [currentTopic]);

  const updateTopic = useCallback(async (id, data) => {
    try {
      const response = await api.put(`/topics/${id}`, data);
      // Update both topics list and current topic
      setTopics(prevTopics => 
        prevTopics.map(topic => 
          topic.id === parseInt(id) ? response.data : topic
        )
      );
      if (currentTopic?.id === parseInt(id)) {
        setCurrentTopic(response.data);
      }
      return response.data;
    } catch (error) {
      console.error("Error updating topic:", error);
      throw error;
    }
  }, [currentTopic]);

  const addSubtopic = useCallback(async (topicId, subtopicData) => {
    try {
      const response = await api.post(`/topics/${topicId}/subtopics`, subtopicData);
      // Update current topic with new subtopic
      if (currentTopic?.id === parseInt(topicId)) {
        setCurrentTopic(prev => ({
          ...prev,
          Subtopics: [...(prev.Subtopics || []), response.data]
        }));
      }
      return response.data;
    } catch (error) {
      console.error("Error adding subtopic:", error);
      throw error;
    }
  }, [currentTopic]);

  const getTopic = useCallback(
    async (id) => {
      // If we already have this topic loaded, return it
      if (currentTopic && currentTopic.id === parseInt(id)) {
        return currentTopic;
      }

      setLoading(true);
      try {
        const response = await api.get(`/topics/${id}`);
        if (!response.data) {
          throw new Error("Topic not found");
        }
        setCurrentTopic(response.data);
        return response.data;
      } catch (error) {
        console.error("Error fetching topic:", error);
        showToast(
          error.response?.data?.message || "Failed to fetch topic",
          "error"
        );
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [currentTopic, showToast]
  );

  // Clear topics (useful for logout)
  const clearTopics = useCallback(() => {
    setTopics([]);
    setCurrentTopic(null);
    topicsLoaded.current = false;
  }, []);

  // Force refresh topics
  const refreshTopics = useCallback(() => {
    topicsLoaded.current = false;
    return getTopics(true);
  }, [getTopics]);

  return (
    <TopicContext.Provider
      value={{
        topics,
        currentTopic,
        loading,
        getTopics,
        getTopic,
        createTopic,
        deleteTopic,
        updateTopic,
        addSubtopic,
        clearTopics,
        refreshTopics,
      }}
    >
      {children}
    </TopicContext.Provider>
  );
};

export const useTopics = () => {
  const context = useContext(TopicContext);
  if (!context) {
    throw new Error("useTopics must be used within a TopicProvider");
  }
  return context;
};
